import React, { createContext, useContext, useState, useMemo, useEffect, useCallback } from 'react';
import { toast, Bounce } from 'react-toastify';
import { defaultPagination } from "utils/table";
import { getUniformItemsApi, getWarehousesApi, getUniformSizesApi, uniformErrorMessage } from "api/services/uniformManagement";
import { getPosition } from "api/services/common";
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMediaQuery } from "react-responsive";

const uniformMgmtContext = createContext();

export const useUniformMgmt = () => {
  return useContext(uniformMgmtContext);
};

export const UniformMgmtProvider = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const navigate = useNavigate();

  // Shared master data
  const [sizes, setSizes] = useState([]);
  const [sizesLoading, setSizesLoading] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  // Starts true, not false: `fetchWarehouses` is kicked off unconditionally
  // in the mount effect below (always runs exactly once, no guard that could
  // skip it), so this is never left stranded — but starting it `false` let
  // the very first frame of a deep link (e.g. straight to bulk-issuance) read
  // as "loaded, zero warehouses" before the fetch had even started.
  const [warehousesLoading, setWarehousesLoading] = useState(true);
  // A failed fetch leaves `warehouses = []` — byte-identical to "loaded fine,
  // nothing carries the role". The four warehouse-role pickers need to tell
  // those apart so a network blip doesn't render as "none configured, go
  // flag one" (see uses of `warehousesError` in the issuance/receiving/
  // transfer screens).
  const [warehousesError, setWarehousesError] = useState(false);

  // Positions a uniform is actually mapped to.
  //
  // Deliberately NOT the shared `state.common.position` list, which is every
  // position in the company: most wear no uniform, and picking one of those
  // in a uniform filter returns an empty grid. The masters screens that
  // create a mapping keep using the full list — an unmapped position is
  // exactly what you select there.
  const [uniformPositions, setUniformPositions] = useState([]);
  // Starts true for the same reason warehousesLoading does: the fetch is
  // kicked off unconditionally below, and `false` would let the first frame
  // of a deep link read as "loaded, no positions wear a uniform".
  const [uniformPositionsLoading, setUniformPositionsLoading] = useState(true);

  // Active tab for the main page
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "items");

  const fetchSizes = useCallback(() => {
    setSizesLoading(true);
    getUniformSizesApi({}).then(response => {
      setSizes(response.data?.data || []);
      setSizesLoading(false);
    }).catch(() => setSizesLoading(false));
  }, []);

  const fetchWarehouses = useCallback(() => {
    setWarehousesLoading(true);
    setWarehousesError(false);
    getWarehousesApi({}).then(response => {
      setWarehouses(response.data?.data || []);
      setWarehousesLoading(false);
    }).catch((e) => {
      setWarehousesLoading(false);
      setWarehousesError(true);
      toast.error(uniformErrorMessage(e, 'Failed to load warehouses'), { position: "top-right", autoClose: 3000, transition: Bounce });
    });
  }, []);

  const fetchUniformPositions = useCallback(() => {
    setUniformPositionsLoading(true);
    // page_size for the reason the common slice documents: PositionList
    // paginates at 100 and only `results` is kept, so without it a company
    // past that silently loses the rest from every dropdown. getPosition's
    // signature is (id, params) — passing params first drops them silently.
    getPosition(null, { has_uniform: 1, page_size: 1000 })
      .then(response => {
        setUniformPositions(response?.data?.results || []);
      })
      .catch(() => setUniformPositions([]))
      .finally(() => setUniformPositionsLoading(false));
  }, []);

  useEffect(() => {
    fetchSizes();
    fetchWarehouses();
    fetchUniformPositions();
  }, []);

  const sizeOptions = useMemo(() => {
    const sizeOrder = ["XS", "S", "M", "L", "XL", "XXL"];
    let sorted = [...sizes].sort((a, b) => sizeOrder.indexOf(a.code) - sizeOrder.indexOf(b.code));
    return sorted.map(item => ({ label: `${item.name} (${item.code})`, value: item.id }));
  }, [sizes]);

  const uniformPositionOptions = useMemo(() => (
    uniformPositions.map(item => ({ label: item.name, value: item.id }))
  ), [uniformPositions]);

  const warehouseOptions = useMemo(() => {
    return warehouses.filter(w => w.status).map(item => ({ label: item.name, value: item.id }));
  }, [warehouses]);

  // Active warehouses carrying each role. A warehouse may be main, issuing,
  // both or neither, and a company may have several of each — there is no
  // cardinality limit or exclusivity rule (that used to apply to the old,
  // single "head office" flag) on either one.
  const issuingWarehouses = useMemo(() => (
    warehouses.filter(w => w.status && w.can_issue_uniform)
  ), [warehouses]);

  const mainWarehouses = useMemo(() => (
    warehouses.filter(w => w.status && w.is_main)
  ), [warehouses]);

  const issuingWarehouseOptions = useMemo(() => (
    issuingWarehouses.map(item => ({ label: item.name, value: item.id }))
  ), [issuingWarehouses]);

  const mainWarehouseOptions = useMemo(() => (
    mainWarehouses.map(item => ({ label: item.name, value: item.id }))
  ), [mainWarehouses]);

  // Which issuing warehouse an issuance screen should preselect.
  //
  // Uniforms are issued to staff from a store, not from the warehouse stock is
  // received into, but nothing stops a site from being both — and the old Head
  // Office was backfilled as `is_main` while keeping the issuing role, so the
  // common shape is one main+issuing warehouse alongside one issuing store.
  // Counting bare options there gives two and preselects nothing, leaving the
  // user to pick from a list with one real answer. So: fall back to the sole
  // non-main issuing warehouse when there is exactly one. Null means "genuinely
  // ambiguous, make the user choose" — never a silent guess between equals.
  const defaultIssuingWarehouseOption = useMemo(() => {
    if (issuingWarehouseOptions.length === 1) return issuingWarehouseOptions[0];
    const stores = issuingWarehouses.filter(w => !w.is_main);
    return stores.length === 1 ? { label: stores[0].name, value: stores[0].id } : null;
  }, [issuingWarehouseOptions, issuingWarehouses]);

  const value = useMemo(() => ({
    sizes, setSizes, sizesLoading, fetchSizes, sizeOptions,
    warehouses, setWarehouses, warehousesLoading, warehousesError, fetchWarehouses, warehouseOptions,
    issuingWarehouses, mainWarehouses, issuingWarehouseOptions, mainWarehouseOptions,
    defaultIssuingWarehouseOption,
    uniformPositions, uniformPositionsLoading, uniformPositionOptions,
    activeTab, setActiveTab,
    isMobile, navigate
  }), [sizes, sizesLoading, warehouses, warehousesLoading, warehousesError, activeTab, isMobile, sizeOptions, warehouseOptions,
    uniformPositions, uniformPositionsLoading, uniformPositionOptions,
    issuingWarehouses, mainWarehouses, issuingWarehouseOptions, mainWarehouseOptions,
    defaultIssuingWarehouseOption]);

  return (
    <uniformMgmtContext.Provider value={value}>
      {children}
    </uniformMgmtContext.Provider>
  );
};
